import React from 'react';
import { useSelector } from 'react-redux';
import { useTranslate } from '@tolgee/react';

import CreatorCard from '../commonElements/creatorCard';
import CreatorTitle from '../commonElements/creatorTitle';
import { LomavisPlatformIcon } from '../../../../lomavisSharedUi/atoms/icons/LomavisPlatformIcon/LomavisPlatformIcon';
import { FACEBOOK, INSTAGRAM } from '../../../../config/constants';
import { getDateTimeFormattedMoment } from '../../../utils/dates/utils';

const BOOST_PLATFORMS = [FACEBOOK, INSTAGRAM] as const;

const PAID_AD_STATUSES = ['none', 'pending', 'active', 'failed'] as const;
type PaidAdStatus = (typeof PAID_AD_STATUSES)[number];

// Backend contract is documented in CropAdditionalData.paid_ad_status, but the
// value crosses a network boundary — a new backend enum value or a garbled
// payload must not blow up the whole creator sidebar. Fall back to 'none' so
// the row is filtered out rather than crashing on `STATUS_ICON[undefined]`.
const normalizePaidAdStatus = (value: unknown): PaidAdStatus => {
  return (PAID_AD_STATUSES as readonly string[]).includes(value as string)
    ? (value as PaidAdStatus)
    : 'none';
};

type PlatformBoostState = {
  platform: (typeof BOOST_PLATFORMS)[number];
  status: PaidAdStatus;
  lastError: string | null;
  failedAt: string | null;
};

const STATUS_BADGE_CLASS: Record<Exclude<PaidAdStatus, 'none'>, string> = {
  active: 'badge-light-success',
  pending: 'badge-light-warning',
  failed: 'badge-light-danger'
};

const STATUS_ICON: Record<
  Exclude<PaidAdStatus, 'none'>,
  { className: string; textClass: string }
> = {
  active: { className: 'fas fa-check-circle', textClass: 'text-success' },
  pending: { className: 'fas fa-clock', textClass: 'text-warning' },
  failed: {
    className: 'fas fa-triangle-exclamation',
    textClass: 'text-danger'
  }
};

const BoostStatus: React.FC = () => {
  const { postData, has_been_published, lomavisCreatorConfig } = useSelector(
    (state: any) => state.lomavisCreator
  );
  const { t } = useTranslate();

  // A reused post is a new post; the boost of the original doesn't apply to it.
  if (!has_been_published || lomavisCreatorConfig?.importing_post) return null;

  const boostStates: PlatformBoostState[] = BOOST_PLATFORMS.map((platform) => {
    const data = postData?.[platform];
    return {
      platform,
      status: normalizePaidAdStatus(data?.paid_ad_status),
      lastError: data?.paid_ad_last_error ?? null,
      failedAt: data?.paid_ad_failed_at ?? null
    };
  }).filter((entry) => entry.status !== 'none');

  if (boostStates.length === 0) return null;

  return (
    <CreatorCard>
      <CreatorTitle title="boost_status" tooltipPosition="right" />
      <div className="d-flex flex-column gap-3">
        {boostStates.map((entry) => (
          <BoostStatusRow key={entry.platform} entry={entry} t={t} />
        ))}
      </div>
    </CreatorCard>
  );
};

const BoostStatusRow: React.FC<{
  entry: PlatformBoostState;
  t: (key: string) => string;
}> = ({ entry, t }) => {
  if (entry.status === 'none') return null;

  const badgeClass = STATUS_BADGE_CLASS[entry.status];
  const { className: iconClass, textClass: iconTextClass } =
    STATUS_ICON[entry.status];

  return (
    <div className="d-flex align-items-start" style={{ minWidth: 0 }}>
      {/* line-height: 1 collapses the inline <i>'s default leading so the
          glyph starts flush with the top of the row instead of sitting
          in the middle of a taller line box. */}
      <div
        className="pe-3 flex-shrink-0"
        style={{ width: 20, lineHeight: 1 }}
      >
        <LomavisPlatformIcon platform={entry.platform} />
      </div>
      {/* min-width: 0 lets the flex child shrink below its content's intrinsic width,
          so long unbreakable error strings inside the <pre> can wrap. */}
      <div
        className="flex-grow-1 text-end"
        style={{ minWidth: 0, overflowWrap: 'anywhere' }}
      >
        <span className={`badge ${badgeClass}`}>
          <i
            className={`${iconClass} ${iconTextClass} me-1`}
            aria-hidden="true"
          ></i>
          {t(`lomavisCreator.boostStatus.${entry.status}`)}
        </span>
        {entry.status === 'failed' && entry.lastError && (
          <pre
            className="mt-1 mb-0 small text-danger text-start"
            style={{
              whiteSpace: 'pre-wrap',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word'
            }}
          >
            {entry.lastError}
          </pre>
        )}
        {entry.status === 'failed' && entry.failedAt && (
          <div className="text-muted small">
            {getDateTimeFormattedMoment(entry.failedAt)}
          </div>
        )}
        {entry.status === 'failed' && (
          <div className="mt-2 text-dark text-start">
            {t('lomavisCreator.boostStatus.failedRetryHint')}
          </div>
        )}
      </div>
    </div>
  );
};

export default BoostStatus;
